import { useReducer } from "react";
import { AgentMessage, ConversationMessage, SetupAnalysisResult } from '@/types/agent/setup';

export type SetupPhase = "image-upload" | "image-results" | "conversation" | "analysis" | "confirmation" | "complete" | "listener-setup";
export type SetupMode = "voice" | "chat" | null;

export interface SetupState {
  conversation: {
    messages: ConversationMessage[];
    showTranscript: boolean;
    conversationId: string | null;
  };
  setup: {
    phase: SetupPhase;
    setupMode: SetupMode;
    analysisResult: SetupAnalysisResult | null;
    isCreatingGenie: boolean;
    isAnalyzing: boolean;
    error: string | null;
    isResuming: boolean;
    createdAgent: any | null;
  };
}

export type SetupAction =
  | { type: "SET_SHOW_TRANSCRIPT"; payload: boolean }
  | { type: "ADD_MESSAGE"; payload: ConversationMessage }
  | { type: "SET_MESSAGES"; payload: ConversationMessage[] }
  | { type: "SET_CONVERSATION_ID"; payload: string | null }
  | { type: "SET_PHASE"; payload: SetupPhase }
  | { type: "SET_SETUP_MODE"; payload: SetupMode }
  | { type: "SET_ANALYSIS_RESULT"; payload: SetupAnalysisResult | null }
  | { type: "SET_IS_CREATING_GENIE"; payload: boolean }
  | { type: "SET_IS_ANALYZING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_IS_RESUMING"; payload: boolean }
  | { type: "SET_CREATED_AGENT"; payload: any | null }
  | { type: "RESET_SETUP" }
  | { type: "HANDLE_OPENAI_PARAMETERS"; payload: any }
  | { type: "HANDLE_OPENAI_CONVERSATION_UPDATE"; payload: AgentMessage[] };

const initialState: SetupState = {
  conversation: {
    messages: [],
    showTranscript: false,
    conversationId: null,
  },
  setup: {
    phase: "conversation",
    setupMode: null,
    analysisResult: null,
    isCreatingGenie: false,
    isAnalyzing: false,
    error: null,
    isResuming: false,
    createdAgent: null,
  },
};

function setupReducer(state: SetupState, action: SetupAction): SetupState {
  switch (action.type) {
    case "SET_SHOW_TRANSCRIPT":
      return {
        ...state,
        conversation: {
          ...state.conversation,
          showTranscript: action.payload,
        },
      };

    case "ADD_MESSAGE":
      return {
        ...state,
        conversation: {
          ...state.conversation,
          messages: [...state.conversation.messages, action.payload],
        },
      };

    case "SET_MESSAGES":
      return {
        ...state,
        conversation: {
          ...state.conversation,
          messages: action.payload,
        },
      };

    case "SET_CONVERSATION_ID":
      return {
        ...state,
        conversation: {
          ...state.conversation,
          conversationId: action.payload,
        },
      };

    case "SET_PHASE":
      return {
        ...state,
        setup: {
          ...state.setup,
          phase: action.payload,
        },
      };

    case "SET_SETUP_MODE":
      return {
        ...state,
        setup: {
          ...state.setup,
          setupMode: action.payload,
        },
      };

    case "SET_ANALYSIS_RESULT":
      return {
        ...state,
        setup: {
          ...state.setup,
          analysisResult: action.payload,
        },
      };

    case "SET_IS_CREATING_GENIE":
      return {
        ...state,
        setup: {
          ...state.setup,
          isCreatingGenie: action.payload,
        },
      };

    case "SET_IS_ANALYZING":
      return {
        ...state,
        setup: {
          ...state.setup,
          isAnalyzing: action.payload,
        },
      };

    case "SET_ERROR":
      return {
        ...state,
        setup: {
          ...state.setup,
          error: action.payload,
        },
      };

    case "SET_IS_RESUMING":
      return {
        ...state,
        setup: {
          ...state.setup,
          isResuming: action.payload,
        },
      };

    case "SET_CREATED_AGENT":
      return {
        ...state,
        setup: {
          ...state.setup,
          createdAgent: action.payload,
        },
      };

    case "RESET_SETUP":
      return {
        ...initialState,
      };

    case "HANDLE_OPENAI_PARAMETERS": {
      const parameters = action.payload;
      const analysis: SetupAnalysisResult = {
        genieName: parameters.genieName || "Unnamed Genie",
        useCase: parameters.useCase || "General assistance",
        language: parameters.language || "English",
        voicePreference: parameters.voicePreference || "Professional",
        systemPrompt: parameters.systemPrompt || `You are ${parameters.genieName || "a helpful assistant"}.`,
        firstMessage: parameters.firstMessage || "Hello! How can I help you today?",
        conversationSettings: {
          maxDuration: 1800,
          turnTimeout: 30,
          silenceTimeout: 300,
        },
        confidence: 0.9,
      };

      return {
        ...state,
        setup: {
          ...state.setup,
          analysisResult: analysis,
        },
      };
    }

    case "HANDLE_OPENAI_CONVERSATION_UPDATE": {
      const convertedMessages: ConversationMessage[] = action.payload.map((msg) => ({
        role: msg.role,
        message: msg.content,
        timestamp: msg.timestamp,
      }));

      return {
        ...state,
        conversation: {
          ...state.conversation,
          messages: convertedMessages,
        },
      };
    }

    default:
      return state;
  }
}

export const useGenieSetupReducer = () => {
  return useReducer(setupReducer, initialState);
};
